import { useMemo } from "react";
import { motion } from "framer-motion";
import {
  Eye,
  Users,
  TrendingUp,
  Lightbulb,
  ArrowDownCircle,
  CalendarDays,
} from "lucide-react";
import { SessionsSparkline } from "./SessionsSparkline";
import { BrandJourneyChart } from "./BrandJourneyChart";
import LiveChatStoryCard from "./LiveChatStoryCard";
import VisitorIdStoryCard from "./VisitorIdStoryCard";
import { ShowingUpScoreboard } from "./glance/ShowingUpScoreboard";
import { WebsiteStoryDropdown } from "./glance/WebsiteStoryDropdown";
import { friendlyRange } from "./compare/headlineTotals";

/**
 * ReportAtAGlance — the 30-second answer at the top of the report.
 *
 * Shows: headline verdict, 3 hero stats, an ROI-readiness score, a Landing-Page score,
 * the single top next move, and 4 jump-to-chapter chips.
 *
 * Data: aggregates the same numbers as ROIStoryHero.
 */
export const ReportAtAGlance = ({
  clientName = "Your Business",
  clientId,
  API,
  reportStart,
  reportEnd,
  closeRatePct = 2.0,
  onImportData,
  // Campaign data (same props as ROIStoryHero)
  mobileSummary,
  metaSummary,
  socialMirrorSummary,
  facebookCampaign,
  ctvPublishers = [],
  googleAdsData,
  tiktokImpressions = 0,
  tiktokClicks = 0,
  tiktokReach = 0,
  pmaxImpressions = 0,
  pmaxClicks = 0,
  appfrontData,
  brandChannelImpressions = 0,
  trafficData,
  analyticsMonthly = [],
  beforeAfter = null,
  liveChatRecords = [],
  visitorIdRecords = [],
  agencyNote = { body: "", updated_at: null },
  onSaveAgencyNote = () => {},
  readOnly = false,
  previousPeriod = null,
  periodControl = null,
}) => {
  // --- Aggregate the same data ROIStoryHero does ---
  const aggregates = useMemo(() => {
    const mobileImpressions = mobileSummary?.overview?.total_impressions || 0;
    const mobileClicks = mobileSummary?.overview?.total_clicks || 0;
    const verifiedVisits = mobileSummary?.overview?.verified_visits || 0;

    const fbImpressions = facebookCampaign?.has_data
      ? facebookCampaign?.overview?.impressions || 0
      : metaSummary?.overview?.total_impressions || 0;
    const fbClicks = facebookCampaign?.has_data
      ? facebookCampaign?.overview?.clicks || 0
      : metaSummary?.overview?.total_clicks || 0;

    const smImpressions = socialMirrorSummary?.overview?.total_impressions || 0;
    const smClicks = socialMirrorSummary?.overview?.total_clicks || 0;

    const ctvImpressions = ctvPublishers.reduce((s, p) => s + (p.impressions || 0), 0);

    const gaImpressions = googleAdsData?.summary?.total_impressions || 0;
    const gaClicks = googleAdsData?.summary?.total_clicks || 0;

    const afImpressions = appfrontData?.summary?.total_impressions || 0;

    const totalImpressions =
      mobileImpressions + fbImpressions + smImpressions + ctvImpressions +
      brandChannelImpressions + gaImpressions + tiktokImpressions + afImpressions + pmaxImpressions;

    const totalClicks = mobileClicks + fbClicks + smClicks + gaClicks + tiktokClicks + pmaxClicks;

    const websiteVisits = trafficData?.totals?.total_sessions || 0;
    const rate = (Number(closeRatePct) || 2.0) / 100;
    const estConversions = websiteVisits > 0 ? Math.round(websiteVisits * rate) : 0;

    const activeChannelCount = [
      mobileImpressions, fbImpressions, ctvImpressions, smImpressions,
      gaImpressions, tiktokImpressions, brandChannelImpressions, afImpressions, pmaxImpressions,
    ].filter((v) => v > 0).length;

    return {
      totalImpressions,
      totalClicks,
      websiteVisits,
      verifiedVisits,
      estConversions,
      activeChannelCount,
      mobileImpressions,
      // Merge Paid into Direct — GA4 routinely misattributes paid clicks as Direct
      // anyway, so showing them separately is misleading. Same logic as
      // BrandJourneyChart so the whole report tells one consistent story.
      directTraffic: (trafficData?.totals?.direct_sessions || 0) + (trafficData?.totals?.paid_sessions || 0),
      organicTraffic: trafficData?.totals?.organic_sessions || 0,
    };
  }, [
    mobileSummary, metaSummary, socialMirrorSummary, facebookCampaign,
    ctvPublishers, googleAdsData, tiktokImpressions, tiktokClicks, pmaxImpressions, pmaxClicks, appfrontData,
    brandChannelImpressions, trafficData, closeRatePct,
  ]);

  const fmt = (n) => {
    if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
    if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
    return Math.round(n).toLocaleString();
  };

  // --- Headline verdict — generic, not date-pegged so it ages well ---
  const verdict = useMemo(() => {
    if (aggregates.totalImpressions === 0 && aggregates.websiteVisits === 0) {
      return {
        line: `${clientName} hasn't reported yet. Upload data to tell the story.`,
        tone: "neutral",
      };
    }
    return {
      line: `How ${clientName} is showing up.`,
      tone: "proof",
    };
  }, [aggregates, clientName]);

  // --- ROI Readiness score (internal, not from AI) ---
  // Proxy: weighted mix of channel diversity + traffic + verified action.
  const roiReadiness = useMemo(() => {
    if (aggregates.totalImpressions === 0) return null;
    const channelScore = Math.min(aggregates.activeChannelCount / 6, 1) * 35; // up to 35
    const trafficScore = aggregates.websiteVisits > 0
      ? Math.min(Math.log10(aggregates.websiteVisits + 1) / 5, 1) * 35 // up to 35
      : 0;
    const actionScore = (aggregates.totalClicks > 0 ? 15 : 0)
      + (aggregates.verifiedVisits > 0 ? 15 : 0);
    return Math.round(channelScore + trafficScore + actionScore);
  }, [aggregates]);

  // --- Score styling ---
  const scoreTone = (s) => {
    if (s === null || s === undefined) return { text: "text-slate-400", ring: "ring-slate-500/30" };
    if (s >= 80) return { text: "text-emerald-300", ring: "ring-emerald-400/40" };
    if (s >= 60) return { text: "text-amber-300",   ring: "ring-amber-400/40" };
    return { text: "text-rose-300", ring: "ring-rose-400/40" };
  };
  const roiTone = scoreTone(roiReadiness);

  // Smooth scroll helper
  const jumpTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const dateRange = friendlyRange(reportStart, reportEnd) || "This period";

  const hasData = !(aggregates.totalImpressions === 0 && aggregates.websiteVisits === 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative z-10 mb-8"
      data-testid="report-at-a-glance"
    >
      <div className="relative overflow-hidden rounded-3xl border border-cyan-300/30 bg-[#0a3d54]">
        {/* Subtle corner glows — brand palette (lime + cyan) */}
        <div className="absolute top-0 right-0 w-[480px] h-[280px] bg-[radial-gradient(ellipse_at_top_right,rgba(163,230,53,0.16),transparent_70%)] pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[360px] h-[220px] bg-[radial-gradient(ellipse_at_bottom_left,rgba(34,211,238,0.12),transparent_70%)] pointer-events-none" />

        <div className="relative p-5 md:p-10">
          {/* Eyebrow — just the date range */}
          <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-5 mb-5 md:mb-6">
            <motion.div
              key={dateRange}
              initial={{ scale: 0.96, opacity: 0.6 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.35 }}
              className="inline-flex self-start items-center gap-2.5 rounded-full bg-white/[0.07] ring-1 ring-lime-300/30 px-4 py-2"
              data-testid="glance-date-range"
            >
              <CalendarDays className="w-4 h-4 md:w-5 md:h-5 text-lime-300" />
              <p className="text-white text-base md:text-xl font-semibold">
                {dateRange}
              </p>
            </motion.div>
            {periodControl}
          </div>

          {/* Headline verdict */}
          <h1 className="text-2xl md:text-5xl font-bold text-white leading-[1.15] md:leading-[1.1] mb-6 md:mb-8 max-w-4xl">
            {verdict.line}
          </h1>

          {!hasData && (
            <div className="bg-[#0c465f] rounded-2xl ring-1 ring-cyan-400/20 p-6 md:p-8 text-center" data-testid="raag-empty-state">
              <div className="w-14 h-14 rounded-full bg-cyan-500/15 flex items-center justify-center mx-auto mb-4">
                <ArrowDownCircle className="w-6 h-6 text-cyan-300" />
              </div>
              <p className="text-white text-lg md:text-xl font-semibold mb-1.5">
                No data imported yet for this period.
              </p>
              <p className="text-cyan-100/70 text-sm max-w-md mx-auto mb-5">
                Upload a PDF or CSV from your ad platforms and this space will fill with {clientName}'s brand impressions, website visits, and estimated new customers.
              </p>
              {onImportData && (
                <button
                  type="button"
                  onClick={onImportData}
                  data-testid="empty-state-import-btn"
                  className="inline-flex items-center gap-2 bg-lime-400 hover:bg-lime-300 text-slate-900 font-bold text-sm px-5 py-2.5 rounded-full shadow-lg shadow-lime-400/20 transition-colors"
                >
                  <ArrowDownCircle className="w-4 h-4 rotate-180" />
                  Import data for {clientName}
                </button>
              )}
            </div>
          )}

          {hasData && (<>
          <ShowingUpScoreboard
            current={{ impressions: aggregates.totalImpressions, visits: aggregates.websiteVisits, clicks: aggregates.totalClicks }}
            previous={previousPeriod}
          />
          </>)}

          {((hasData && analyticsMonthly.length > 0) || liveChatRecords.length > 0 || visitorIdRecords.length > 0) && (
            <WebsiteStoryDropdown
              visits={aggregates.websiteVisits}
              hasChats={liveChatRecords.length > 0}
              hasVisitorId={visitorIdRecords.length > 0}
            >
              {hasData && <BrandJourneyChart data={analyticsMonthly} clientName={clientName} clientId={clientId} API={API} readOnly={readOnly} reportStart={reportStart} reportEnd={reportEnd} />}
              <LiveChatStoryCard records={liveChatRecords} clientName={clientName} />
              <VisitorIdStoryCard records={visitorIdRecords} clientName={clientName} />
            </WebsiteStoryDropdown>
          )}

          {/* Jump to section chips removed per user request (Apr 20, 2026) */}
        </div>
      </div>
    </motion.div>
  );
};

// --- Sub-components ---

// Brand palette accents — matches ChapterDropdown + score cards
const ACCENTS = {
  cyan: { icon: "text-cyan-300", iconBg: "bg-cyan-500/15", ring: "ring-cyan-400/25", label: "text-cyan-200/80" },
  teal: { icon: "text-teal-300", iconBg: "bg-teal-500/15", ring: "ring-teal-400/25", label: "text-teal-200/80" },
  lime: { icon: "text-lime-300", iconBg: "bg-lime-400/15", ring: "ring-lime-400/30", label: "text-lime-200/80" },
};

const StatTile = ({ icon: Icon, accent, value, label, sub, tooltip, children }) => {
  const a = ACCENTS[accent] || ACCENTS.cyan;
  return (
    <div className={`relative bg-[#0c465f] rounded-2xl ring-1 ${a.ring} p-4 md:p-5 flex flex-col gap-2`}>
      <div className={`w-9 h-9 rounded-xl ${a.iconBg} flex items-center justify-center`}>
        <Icon className={`w-4 h-4 ${a.icon}`} />
      </div>
      <div>
        <p className="text-3xl md:text-4xl font-black text-white tabular-nums leading-none">{value}</p>
        <div className="flex items-center gap-1 mt-1.5">
          <p className={`${a.label} text-xs md:text-sm font-medium`}>{label}</p>
          {tooltip && (
            <span
              className="relative inline-flex items-center justify-center w-4 h-4 rounded-full bg-white/5 text-slate-400 hover:text-white hover:bg-white/15 text-[9px] font-bold cursor-help transition-colors group/tip"
              tabIndex={0}
              aria-label={tooltip}
              data-testid="stat-tile-info"
            >
              i
              <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 bg-slate-900 text-slate-100 text-[11px] font-normal leading-relaxed normal-case tracking-normal p-3 rounded-lg shadow-xl ring-1 ring-white/10 opacity-0 invisible group-hover/tip:opacity-100 group-hover/tip:visible group-focus/tip:opacity-100 group-focus/tip:visible transition-opacity z-30">
                {tooltip}
              </span>
            </span>
          )}
        </div>
        {sub && <p className="text-slate-400 text-[11px] mt-0.5">{sub}</p>}
        {children}
      </div>
    </div>
  );
};
